import { CloseOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import {
	Button,
	Card,
	Col,
	message,
	Modal,
	Row,
	Spin,
	Typography,
	Input,
	Table,
	Tooltip,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box } from 'atoms';
import { ACL } from 'components/common';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import { getClientContractByIdV2 } from 'store/clientSliceV2';
import {
	approveClientContract,
	rejectClientContract,
	getApprovalDetailsV2,
} from 'store/approvalSliceV2';

const { Title, Text } = Typography;

const PageContainer = styled(Box)`
	padding: 24px;
	background: ${(props) => (props.fromApproval ? '#fff' : '#f5f5f5')};
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const HeaderRight = styled.div`
	display: flex;
	gap: 12px;
`;

const ContractId = styled(Title)`
	margin: 0 !important;
	font-size: 20px;
	font-weight: 600;
`;

const CloseIconButton = styled(Button)`
	border: none;
	box-shadow: none;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;

	&:hover {
		background-color: #f5f5f5;
	}
`;

const ActionButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e8e8e8;
`;

const RejectButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
	}
`;

const ApproveButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #52c41a;
	color: #fff;
	border: 1.5px solid #52c41a;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #73d13d;
		border-color: #73d13d;
	}

	&:active {
		background: #389e0d;
		border-color: #389e0d;
	}
`;

const DetailsCard = styled(Card)`
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-bottom: 24px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 4px;
`;

const FieldValue = styled(Text)`
	font-size: 14px;
	color: #000;
	font-weight: 500;
	display: block;
`;

const { TextArea } = Input;

const APPROVALS_CLIENT_CONTRACT_PATH = '/approvals/client-contract';

const formatDate = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY');
};

const MONEY_FIELDS = new Set([
	'vehicle_cost',
	'loader_cost',
	'delivery_associate_cost',
	'extra_charges_per_km',
	'extra_charges_per_hour',
	'extra_charges_per_trip',
	'charges_per_km',
	'other_expenses_value',
	'fixed_rate_per_day',
]);

const titleCase = (key = '') =>
	key
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.trim();

const isIsoDateString = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(val);

// Document keys are stored as "{uuid}-{uuid}-{original filename}"; strip every
// leading UUID prefix so only the human-readable filename remains.
const UUID_PREFIX = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;

const stripUuidPrefixes = (key) => {
	let result = String(key);
	while (UUID_PREFIX.test(result)) {
		result = result.replace(UUID_PREFIX, '');
	}
	return result;
};

// Document values can be nested (e.g. { signed_contract_file: { document_key } });
// dig out the first document_key found at any depth.
const findDocumentKey = (obj) => {
	if (!obj || typeof obj !== 'object') return null;
	if (typeof obj.document_key === 'string') return obj.document_key;
	return Object.values(obj).reduce((found, v) => found || findDocumentKey(v), null);
};

const formatFieldValue = (val, field) => {
	if (val === null || val === undefined || val === '') return '-';
	if (typeof val === 'boolean') return val ? 'Yes' : 'No';
	if (isIsoDateString(val)) {
		const m = moment(val);
		if (m.isValid()) return m.format('DD-MM-YYYY');
	}
	if (typeof val === 'object') {
		const docKey = findDocumentKey(val);
		if (docKey) return stripUuidPrefixes(docKey);
		try {
			return JSON.stringify(val);
		} catch (e) {
			return String(val);
		}
	}
	if (MONEY_FIELDS.has(field) && Number.isFinite(Number(val))) {
		return `₹${val}`;
	}
	return String(val);
};

const buildEditFlowRows = (fieldDiff, currentValues) => {
	if (!fieldDiff || typeof fieldDiff !== 'object') return [];
	return Object.entries(fieldDiff).map(([field, entry]) => ({
		key: field,
		field,
		currentValue: entry?.old ?? currentValues?.[field],
		proposedValue: entry?.new,
	}));
};

const DocumentEyeIcon = styled(EyeOutlined)`
	color: #1890ff;
	cursor: pointer;
	margin-left: 8px;
	font-size: 16px;
`;
const renderOrderRates = (val) => {
	if (!Array.isArray(val) || val.length === 0) return '-';
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{val.map((line, i) => (
				// eslint-disable-next-line react/no-array-index-key
				<span key={line?.rate_id || `line-${i}`}>
					{line?.rate_id || `Line ${i + 1}`}: ₹{line?.rate}
				</span>
			))}
		</div>
	);
};

const renderValueCell = (val, field, onViewDocument) => {
	if (field === 'order_rates' && Array.isArray(val)) {
		return renderOrderRates(val);
	}
	const docKey = val && typeof val === 'object' ? findDocumentKey(val) : null;
	if (docKey) {
		const fileName = stripUuidPrefixes(docKey);
		return (
			<span>
				{fileName}
				<Tooltip title="View document">
					<DocumentEyeIcon onClick={() => onViewDocument(docKey, fileName)} />
				</Tooltip>
			</span>
		);
	}
	return formatFieldValue(val, field);
};

const getEditFlowColumns = (onViewDocument) => [
	{
		title: 'Field',
		dataIndex: 'field',
		key: 'field',
		render: (text) => titleCase(text),
	},
	{
		title: 'Current Value',
		dataIndex: 'currentValue',
		key: 'currentValue',
		render: (val, record) => renderValueCell(val, record?.field, onViewDocument),
	},
	{
		title: 'Proposed Value',
		dataIndex: 'proposedValue',
		key: 'proposedValue',
		render: (val, record) => renderValueCell(val, record?.field, onViewDocument),
	},
];

const formatTempControlled = (value) => {
	if (value === true || value === 'true' || value === 'Yes' || value === 'yes' || value === 1) {
		return 'Yes';
	}
	if (value === false || value === 'false' || value === 'No' || value === 'no' || value === 0) {
		return 'NO';
	}
	return value || '-';
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function ClientContractDetailsPageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	const [documentPreview, setDocumentPreview] = useState({
		visible: false,
		fileKey: null,
		fileName: '',
	});

	const handleViewDocument = (fileKey, fileName) => {
		if (!fileKey) {
			message.warning('Document not available');
			return;
		}
		setDocumentPreview({ visible: true, fileKey, fileName });
	};

	const closeDocumentPreview = () =>
		setDocumentPreview({ visible: false, fileKey: null, fileName: '' });

	// Check if accessed from approval page
	const isApprovalRoute = location.pathname.startsWith('/approvals/client-contract');
	const fromApproval = location.state?.fromApproval || isApprovalRoute;
	const approvalIdFromState = location.state?.approvalId || null;
	const entityIdFromState = location.state?.entityId || null;
	// If accessed from approval route, the id param is the entity_id (contract ID)
	// approval_id is passed in state
	const entityId = isApprovalRoute ? id || entityIdFromState : id;
	const approvalId = approvalIdFromState;

	useEffect(() => {
		if (entityId) {
			if (isApprovalRoute) {
				fetchApprovalDetails();
			} else {
				fetchDetails();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityId, isApprovalRoute]);

	/**
	 * Extract temperature_controlled value with fallback logic
	 */
	const getTempControlledValue = (entityData, contract) => {
		if (entityData?.temperature_controlled !== undefined) {
			return entityData.temperature_controlled;
		}
		if (contract?.temperature_controlled !== undefined) {
			return contract.temperature_controlled;
		}
		if (contract?.temp_controlled !== undefined) {
			return contract.temp_controlled;
		}
		return null;
	};

	/**
	 * Merge approval data with contract data
	 */
	const mergeApprovalAndContractData = (approvalData, contract) => {
		const entityData = approvalData.approval?.entity_data || {};
		const entityDetails = approvalData.entity_details || {};
		const relatedData = approvalData.related_data || {};

		return {
			...contract,
			...entityDetails,
			approval_type: approvalData.approval?.approval_type,
			can_decide: approvalData.approval?.can_decide,
			billing_type: entityData.billing_type || contract?.billing_type || '-',
			contract_date: entityData.contract_date || contract?.contract_date || null,
			expiry_date: entityData.expiry_date || contract?.expiry_date || null,
			temp_controlled: getTempControlledValue(entityData, contract),
			vehicle_type: entityData.vehicle_type || contract?.vehicle_type_name || '-',
			city: entityDetails.city || entityData.city || contract?.city || '-',
			created_by:
				approvalData.approval?.requested_by_name ||
				entityDetails.created_by ||
				contract?.created_by ||
				'-',
			client_name:
				relatedData.client?.name ||
				relatedData.client?.client_name ||
				entityDetails.client_name ||
				contract?.client_name ||
				'-',
			project_name:
				relatedData.project?.name ||
				relatedData.project?.project_name ||
				entityDetails.project_name ||
				contract?.project_name ||
				'-',
		};
	};

	const fetchApprovalDetails = async () => {
		setLoading(true);
		try {
			let contract = null;
			let approvalData = null;

			// Fetch approval details first to get entity_data
			if (approvalId) {
				approvalData = await dispatch(getApprovalDetailsV2({ approval_id: approvalId }));
			}

			// Fetch contract details using entity_id
			if (entityId) {
				contract = await dispatch(getClientContractByIdV2({ id: entityId }));
			}

			// If we have approval data, use entity_data as primary source
			if (approvalData && approvalData.approval) {
				const mergedDetails = mergeApprovalAndContractData(approvalData, contract);
				setDetails(mergedDetails);
			} else if (contract) {
				// Fallback: use contract data directly, map temperature_controlled
				const contractDetails = {
					...contract,
					temp_controlled: getTempControlledValue({}, contract),
				};
				setDetails(contractDetails);
			} else {
				message.error('Contract not found');
				navigate(APPROVALS_CLIENT_CONTRACT_PATH);
			}
		} catch (error) {
			console.error('Error fetching approval details:', error);
			message.error('Failed to fetch approval details');
		} finally {
			setLoading(false);
		}
	};

	const fetchDetails = async () => {
		setLoading(true);
		try {
			// Use v2 API to fetch contract by entity_id
			const contract = await dispatch(getClientContractByIdV2({ id: entityId }));

			if (contract) {
				setDetails(contract);
			} else {
				message.error('Contract not found');
				navigate('/client-contracts');
			}
		} catch (error) {
			console.error('Error fetching details:', error);
			message.error('Failed to fetch contract details');
		} finally {
			setLoading(false);
		}
	};

	const handleApproveClick = () => {
		setApproveModalVisible(true);
		setComments('');
	};

	const handleRejectClick = () => {
		setRejectModalVisible(true);
		setRejectionReason('');
	};

	const handleApproveConfirm = async () => {
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				approveClientContract({
					approval_id: approvalId,
					comments,
				})
			);

			if (result.success) {
				setApproveModalVisible(false);
				setComments('');
				message.success('Contract approved successfully');
				navigate(APPROVALS_CLIENT_CONTRACT_PATH);
			}
		} catch (error) {
			console.error('Error approving contract:', error);
			message.error('Failed to approve contract');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRejectConfirm = async () => {
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		if (!rejectionReason.trim()) {
			message.error('Rejection reason is required');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				rejectClientContract({
					approval_id: approvalId,
					rejection_reason: rejectionReason,
				})
			);

			if (result.success) {
				setRejectModalVisible(false);
				setRejectionReason('');
				message.success('Contract rejected successfully');
				navigate(APPROVALS_CLIENT_CONTRACT_PATH);
			}
		} catch (error) {
			console.error('Error rejecting contract:', error);
			message.error('Failed to reject contract');
		} finally {
			setActionLoading(false);
		}
	};

	if (loading) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Spin size="large" tip="Loading contract details..." />
				</Box>
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Text>Contract not found</Text>
				</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer $fromApproval={fromApproval}>
			<HeaderContainer>
				<HeaderLeft>
					<ContractId level={4}>Request ID: {details.id}</ContractId>
				</HeaderLeft>
				<HeaderRight>
					{fromApproval ? (
						<CloseIconButton onClick={() => navigate(APPROVALS_CLIENT_CONTRACT_PATH)}>
							<CloseOutlined style={{ fontSize: '20px', color: '#000' }} />
						</CloseIconButton>
					) : null}
				</HeaderRight>
			</HeaderContainer>

			<DetailsCard>
				<Row gutter={[24, 16]}>
					<Col span={8}>
						<FieldLabel>Client Name</FieldLabel>
						<FieldValue>
							<strong>{details.client_name || '-'}</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Project Name</FieldLabel>
						<FieldValue>
							<strong>
								{details.project_location_code || details.project_name || '-'}
							</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Billing Type</FieldLabel>
						<FieldValue>
							<strong>{details.billing_type || details.contract_type || '-'}</strong>
						</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Contract Date</FieldLabel>
						<FieldValue>{formatDate(details.contract_date)}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Expiry Date</FieldLabel>
						<FieldValue>{formatDate(details.expiry_date)}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Temp Controlled</FieldLabel>
						<FieldValue>
							<strong>{formatTempControlled(details.temp_controlled)}</strong>
						</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Vehicle Type</FieldLabel>
						<FieldValue>{details.vehicle_type || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>City</FieldLabel>
						<FieldValue>{details.city || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Created By</FieldLabel>
						<FieldValue>{details.created_by || '-'}</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{details.fixed_rate && (
				<DetailsCard>
					<Row gutter={[24, 16]}>
						<Col span={8}>
							<FieldLabel>Fixed Rate</FieldLabel>
							<FieldValue>
								{details.fixed_rate
									? `₹ ${parseFloat(details.fixed_rate).toFixed(2)}`
									: '-'}
							</FieldValue>
						</Col>
						{details.info && (
							<>
								<Col span={8}>
									<FieldLabel>Extra Charge Per Hour</FieldLabel>
									<FieldValue>
										{details.info.extra_charge_per_hr
											? `₹ ${parseFloat(
													details.info.extra_charge_per_hr
											  ).toFixed(2)}`
											: '-'}
									</FieldValue>
								</Col>
								<Col span={8}>
									<FieldLabel>Extra Charge Per KM</FieldLabel>
									<FieldValue>
										{details.info.extra_charge_per_km
											? `₹ ${parseFloat(
													details.info.extra_charge_per_km
											  ).toFixed(2)}`
											: '-'}
									</FieldValue>
								</Col>
							</>
						)}
					</Row>
					{details.info && (
						<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
							<Col span={8}>
								<FieldLabel>Threshold KM Per Day</FieldLabel>
								<FieldValue>
									{details.info.threshold_km_per_day
										? `${details.info.threshold_km_per_day} km`
										: '-'}
								</FieldValue>
							</Col>
							<Col span={8}>
								<FieldLabel>Threshold KM Per Month</FieldLabel>
								<FieldValue>
									{details.info.threshold_km_per_month
										? `${details.info.threshold_km_per_month} km`
										: '-'}
								</FieldValue>
							</Col>
						</Row>
					)}
				</DetailsCard>
			)}

			{details.fixed_rate_per_day != null && (
				<DetailsCard>
					<Row gutter={[24, 16]}>
						<Col span={8}>
							<FieldLabel>Fixed Rate Per Day</FieldLabel>
							<FieldValue>
								<strong>
									₹ {parseFloat(details.fixed_rate_per_day).toFixed(2)}
								</strong>
							</FieldValue>
						</Col>
					</Row>
				</DetailsCard>
			)}

			{details.approval_type === 'client_contract_edit' && details.field_diff && (
				<>
					<DetailsCard title="Edit Request" headStyle={{ fontWeight: 600, fontSize: 16 }}>
						<Row gutter={[24, 16]}>
							<Col span={8}>
								<FieldLabel>Submitted By</FieldLabel>
								<FieldValue>{details.editor_name || '-'}</FieldValue>
							</Col>
							<Col span={8}>
								<FieldLabel>Submitted At</FieldLabel>
								<FieldValue>
									{details.submitted_at
										? moment(details.submitted_at).format('DD-MM-YYYY HH:mm')
										: '-'}
								</FieldValue>
							</Col>
							<Col span={8}>
								<FieldLabel>Reason</FieldLabel>
								<FieldValue>{details.editor_comments || '-'}</FieldValue>
							</Col>
						</Row>
					</DetailsCard>

					<DetailsCard>
						<Table
							rowKey="key"
							columns={getEditFlowColumns(handleViewDocument)}
							dataSource={buildEditFlowRows(
								details.field_diff,
								details.current_values
							)}
							pagination={false}
							size="middle"
						/>
					</DetailsCard>
				</>
			)}

			{fromApproval && details?.can_decide === true && (
				<ActionButtonContainer>
					<ACL action="update" module="handle_client_contract_approval">
						<ApproveButtonStyled
							icon={<CheckOutlined />}
							onClick={handleApproveClick}
							loading={actionLoading}
							disabled={actionLoading}
						>
							Approve
						</ApproveButtonStyled>
					</ACL>
					<ACL action="update" module="handle_client_contract_approval">
						<RejectButtonStyled
							icon={<CloseOutlined />}
							onClick={handleRejectClick}
							loading={actionLoading}
							disabled={actionLoading}
						>
							Reject
						</RejectButtonStyled>
					</ACL>
				</ActionButtonContainer>
			)}

			{/* Approve Modal */}
			<Modal
				title={null}
				open={approveModalVisible}
				onCancel={() => {
					setApproveModalVisible(false);
					setComments('');
				}}
				footer={null}
				centered
				width={400}
			>
				<Box textAlign="center">
					<Title level={4} style={{ marginBottom: 16 }}>
						Are you sure?
					</Title>
					<Text
						display="block"
						style={{
							fontSize: '14px',
							color: '#666',
							marginBottom: 24,
							lineHeight: 1.5,
						}}
					>
						This contract will be approved and you won&apos;t be able to revert this
						change.
					</Text>
					<Box mb={3} textAlign="left">
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments <Text color="#ff4d4f">*</Text>
						</Text>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter any comments for this approval..."
							maxLength={1000}
							showCount
							status={!comments.trim() && comments !== '' ? 'error' : ''}
						/>
						{!comments.trim() && comments !== '' && (
							<Text fontSize="12px" color="#ff4d4f" mt={1} display="block">
								Comments are required
							</Text>
						)}
					</Box>
					<Box display="flex" justifyContent="center" gap="12px" mt={3}>
						<Button
							onClick={() => {
								setApproveModalVisible(false);
								setComments('');
							}}
							disabled={actionLoading}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							onClick={handleApproveConfirm}
							loading={actionLoading}
							disabled={actionLoading || !comments.trim()}
						>
							Approve
						</Button>
					</Box>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Client Contract"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !rejectionReason.trim(),
					danger: true,
				}}
			>
				<Box mb={3}>
					<Text fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject this client contract?
					</Text>
					{details && (
						<Box mt={2} p={2} background="#f5f5f5" borderRadius="4px">
							<Text fontSize="12px" color="#666" display="block">
								Contract ID: {details.id}
							</Text>
							<Text fontSize="12px" color="#666" display="block">
								Client: {details.client_name || '-'}
							</Text>
						</Box>
					)}
				</Box>
				<Box>
					<Text fontSize="14px" color="#333" mb={1} display="block">
						Rejection Reason <Text color="#ff4d4f">*</Text>
					</Text>
					<TextArea
						rows={4}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="Enter reason for rejection..."
						maxLength={1000}
						showCount
						status={!rejectionReason.trim() && rejectionReason !== '' ? 'error' : ''}
					/>
					{!rejectionReason.trim() && rejectionReason !== '' && (
						<Text fontSize="12px" color="#ff4d4f" mt={1} display="block">
							Rejection reason is required
						</Text>
					)}
				</Box>
			</Modal>

			{/* Document Preview Modal */}
			<FilePreviewModal
				visible={documentPreview.visible}
				fileKey={documentPreview.fileKey}
				fileName={documentPreview.fileName || 'Document'}
				entity="client_contract"
				onClose={closeDocumentPreview}
			/>
		</PageContainer>
	);
}

export default ClientContractDetailsPageV2;
